import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

/**
 * Control-size scale (CH-22 / PP-31) — same three names and heights as
 * `buttonVariants` and `inputVariants` (`default` 36 / `sm` 32 / `xs` 28)
 * so a compact row of mixed controls lines up. `default` is unchanged and
 * stays the default; see input.tsx for why the default does not move.
 */
const selectTriggerVariants = cva(
  "flex w-full items-center justify-between rounded-md border border-input bg-background text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
  {
    variants: {
      size: {
        default: "h-9 px-3 py-2",
        sm: "h-8 px-2.5 py-1.5",
        xs: "h-7 px-2 py-1",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, children, size, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    data-slot="select-trigger"
    className={cn(selectTriggerVariants({ size }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className={cn("opacity-50", size === "xs" ? "h-3.5 w-3.5" : "h-4 w-4")} />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      // The scroll-up button sits ABOVE the Viewport in the
      // SelectContent flex column. `-mb-1` pulls its bottom edge 4 px
      // down into the Viewport so its `bg-popover` masks the strip
      // where the Viewport's top row could otherwise be visible
      // beneath. `relative z-10` ensures it paints on top of the
      // Viewport. The scroll button lives outside the Viewport's
      // overflow:auto, so this negative margin isn't clipped.
      "relative z-10 -mb-1 flex cursor-default items-center justify-center py-1 bg-popover",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      // The scroll-down button sits BELOW the Viewport in the
      // SelectContent flex column. `-mt-1` pulls its top edge 4 px up
      // into the Viewport so its `bg-popover` masks the strip where the
      // Viewport's bottom row (or the sticky "Show all operators…"
      // footer) could otherwise leak through to the chevron icon. The
      // scroll button lives outside the Viewport's overflow:auto, so
      // this negative margin isn't clipped.
      "relative z-10 -mt-1 flex cursor-default items-center justify-center py-1 bg-popover",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

type SelectContentExtraProps = {
  /**
   * Override the Radix Viewport's className. Defaults to `"p-1"`. Pass
   * an empty string (or a different padding) when the children need to
   * span the popover's full width edge-to-edge (e.g., a sticky footer
   * with `bg-popover` that must reach the chevron container; or a
   * full-bleed warning banner). Tailwind arbitrary-selector overrides
   * on `className` are fragile across v3/v4 important-modifier syntax,
   * so this prop is the canonical way to override viewport padding.
   */
  viewportClassName?: string;
  /**
   * When `true` (default), the content is rendered into a body-level portal
   * via Radix's `<Portal>`. Set to `false` to render the content inline as a
   * descendant of the trigger's DOM ancestor — required when the host is a
   * modal Radix Dialog that locks `pointer-events: none` on `<body>`, since
   * body-level portals would otherwise inherit the lock.
   */
  portalled?: boolean;
};

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & SelectContentExtraProps
>(({ className, children, position = "popper", viewportClassName, portalled = true, ...props }, ref) => {
  const content = (
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        // Entrance: instant-feeling fade only (no zoom/slide), ≤100ms —
        // native feel. Exit keeps the soft default fade+zoom (CH-19).
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md [&[data-state=open]]:animate-in [&[data-state=closed]]:animate-out [&[data-state=closed]]:fade-out-0 [&[data-state=open]]:fade-in-0 [&[data-state=closed]]:zoom-out-95 [&[data-state=open]]:duration-100",
        position === "popper" &&
          "[&[data-side=bottom]]:translate-y-1 [&[data-side=left]]:-translate-x-1 [&[data-side=right]]:translate-x-1 [&[data-side=top]]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          viewportClassName ?? "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  );
  return portalled ? <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal> : content;
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground [&[data-disabled]]:pointer-events-none [&[data-disabled]]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  selectTriggerVariants,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
} 
